<template>
  <div class="flex items-center justify-between">
    <div class="relative inline-block text-left">
      <div class="pb-3 space-x-3">
        <h3 class="inline-flex font-semibold">Viewing</h3>
        
        <SaSelect
          id="filter"
          name="filter"
          v-model="selectedFilter"
          :options="filterOptions"
        >
          <template #option="{ option }">
              {{ option.name }}
          </template>

          <template #selected="{ option }">
            {{ option.name ?? "Select an option" }}
          </template>
        </SaSelect>
      </div>
    </div>
  </div>
  <div
    class="relative z-0 flex-1 gap-2 overflow-y-auto focus:outline-hidden xl:order-last bg-gray-50"
  >
    <div class="relative ">
      <div class="h-full border rounded-lg">
        <div class="px-6 py-2 sm:py-4 md:p-8">
          <div class="learningModuleTab md:h-full">
            <div class="w-full my-6">
              <ul
                role="list"
                class="grid grid-cols-1 gap-5 mt-3 sm:gap-6 sm:grid-cols-2 lg:grid-cols-4"
                v-if="bookmarksLoaded && filteredBookmarks.length > 0"
              >
                <li
                  v-for="bookmark in filteredBookmarks"
                  :key="bookmark.id"
                  class="flex col-span-1 rounded-md shadow-sm"
                >
                  <div
                    class="flex items-center justify-center w-16 text-sm font-medium text-white bg-white shrink-0 rounded-l-md"
                  >
                    <a :href="bookmark.targetUrl"
                      ><img :alt="bookmark.title" :src="bookmark.imageUrl"
                    /></a>
                  </div>
                  <div
                    class="flex items-center justify-between flex-1 truncate bg-white border-t border-b border-r border-gray-200 rounded-r-md"
                  >
                    <div class="flex-1 px-4 py-2 text-sm truncate">
                      <a
                        :href="bookmark.targetUrl"
                        class="font-medium text-gray-900 hover:text-gray-600"
                      >
                        {{ bookmark.title }}
                      </a>
                      <p class="text-gray-600 truncate">
                        {{ bookmark.subtitle }}
                      </p>
                    </div>
                  </div>
                </li>
              </ul>
              <div v-else-if="bookmarksLoaded && filteredBookmarks.length < 1">
                <div class="text-center">
                  <h2
                    class="mt-2 text-base font-semibold leading-6 text-gray-600"
                  >
                    You have not bookmarked any content yet.
                  </h2>
                </div>
              </div>
              <ul
                role="list"
                class="grid grid-cols-1 gap-5 mt-3 sm:gap-6 sm:grid-cols-2 lg:grid-cols-4"
                v-else
              >
                <!-- LOADING SKELETON CARDS -->
                <li role="status" class="text-center shadow-sm animate-pulse ">
                  <div
                    class="relative flex items-center space-x-3 bg-white border border-clay-500 shadow-sm focus-within:ring-2 focus-within:ring-electric-500 focus-within:ring-offset-2 hover:border-gray-400"
                  >
                    <div class="shrink-0">
                      <div
                        class="flex items-center justify-center mx-auto bg-gray-300 w-14 h-14 dark:bg-gray-700"
                      >
                        <svg
                          class="w-6 h-6 text-gray-200 dark:text-gray-600"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                          fill="currentColor"
                          viewBox="0 0 640 512"
                        >
                          <path
                            d="M480 80C480 35.82 515.8 0 560 0C604.2 0 640 35.82 640 80C640 124.2 604.2 160 560 160C515.8 160 480 124.2 480 80zM0 456.1C0 445.6 2.964 435.3 8.551 426.4L225.3 81.01C231.9 70.42 243.5 64 256 64C268.5 64 280.1 70.42 286.8 81.01L412.7 281.7L460.9 202.7C464.1 196.1 472.2 192 480 192C487.8 192 495 196.1 499.1 202.7L631.1 419.1C636.9 428.6 640 439.7 640 450.9C640 484.6 612.6 512 578.9 512H55.91C25.03 512 .0006 486.1 .0006 456.1L0 456.1z"
                          />
                        </svg>
                      </div>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div
                        class="w-24 h-2 mb-4 bg-gray-200 rounded-full dark:bg-gray-700"
                      ></div>
                      <div
                        class="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700"
                      ></div>
                    </div>
                  </div>
                </li>
                <!-- /LOADING SKELETON CARDS -->
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
// @ts-ignore
import { computed, onMounted, Ref, ref } from "vue";
import { client } from "@/shared";
// @ts-ignore
import SaCombobox from "@/main/components/SaCombobox.vue";
import { GetBookmarks, GetBookmarksResponse, YourBookmarkDto } from "@/dtos";
import SaSelect from "@/main/components/SaSelect.vue";

let responseStatus = ref(null);
let bookmarks: Ref<YourBookmarkDto[]> = ref([]);

const filterOptions = [
  { name: "All", value: "All" },
  { name: "Auditions & Jobs", value: "Auditions & Jobs" },
  { name: "Blog Posts", value: "Blogs" },
  { name: "Characters", value: "Characters" },
  { name: "College Programs", value: "College Programs" },
  { name: "Learning Modules", value: "Learning Modules" },
  { name: "Monologues", value: "Monologues" },
  { name: "Playwrights & Composers", value: "Writers" },
  { name: "Scenes", value: "Scenes" },
  { name: "Shows", value: "Shows" },
  { name: "Songs", value: "Songs" },
  { name: "Talent", value: "Talent" },
  { name: "Theatre Companies", value: "Companies" }
];
let selectedFilter = ref({ name: "All", value: "All" });
let bookmarksLoaded: Ref<Boolean> = ref(false);

onMounted(async () => {
  await getBookmarks();
});

const getBookmarks = async () => {
  bookmarksLoaded.value = false;
  await client.get<GetBookmarksResponse>(new GetBookmarks()).then(response => {
    bookmarksLoaded.value = true;

    bookmarks.value = response.bookmarks;
  });
};

let filteredBookmarks = computed(() =>
  selectedFilter.value.name === "All"
    ? bookmarks.value
    : bookmarks.value.filter((bookmark: YourBookmarkDto) => {
        return bookmark.type.includes(selectedFilter.value.name);
      })
);
</script>
<style></style>
